import { useFormContext, Controller } from 'react-hook-form';
// @mui
import TextField, { TextFieldProps } from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { useState } from 'react';
import Decimal from 'decimal.js';

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  transform?: { from: string; to: string };
  initValue?: number;
  defaultValue?: string | number | any;
  onFilter?: ((item: any) => void) | undefined;
};

// Type: [number, text, percent]

export default function RHFTextField({
  name,
  helperText,
  type = 'text',
  transform = undefined,
  InputProps = undefined,
  initValue = undefined,
  onFilter,
  ...other
}: Props) {
  const { control } = useFormContext();
  const [valueInput, setValueInput] = useState<string | number | any>(
    initValue && type === 'percent' ? new Decimal(initValue).times(100) : ''
  );

  const propsPercent =
    type === 'percent'
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <InputLabel htmlFor="floating-label">%</InputLabel>
            </InputAdornment>
          ),
        }
      : InputProps;

      return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          type={type === 'percent' || type === 'number' ? 'text' : type}
          value={
            valueInput ||
            (type === 'percent'
              ? field.value && new Decimal(Number(field.value)).times(100)
              : field.value)
          }
          onChange={(event) => {
            if(onFilter) onFilter(event.target.value)

            if (transform) {
              const { from, to } = transform;
              event.target.value = event.target.value.replace(from, to);
            }

            const sanitizedValue =
              type === 'percent' || type === 'number'
                ? event.target.value.replace(/[^0-9.]/g, '')
                : event.target.value;

            if (type === 'number') {
              return field.onChange(sanitizedValue);
            }

            if (type === 'percent') {
              setValueInput(sanitizedValue);
              return field.onChange(new Decimal(Number(sanitizedValue)).dividedBy(100));
            }

            return field.onChange(sanitizedValue);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && type === 'percent' && valueInput.length <= 1) {
              setValueInput('');
              field.onChange('');
            }
          }}
          InputProps={propsPercent}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        />
      )}
    />
  );
}
